import type { CatalogLevel } from './catalog-api';

/**
 * Onglets du détail d'une entrée du catalogue. L'onglet actif vit dans l'URL (`?tab=`).
 *   - catalogue (couche) et base : Contenu (les enfants), Résumé, Définition ;
 *   - table (standard Unity Catalog Explorer / DataHub, 2026-10-19) : Vue d'ensemble, Schéma,
 *     Aperçu, Statistiques, Historique, Lignage, Usages, Définition ;
 *   - champ : Résumé, Définition (pas d'enfants).
 */
export const CATALOG_TABS = [
  'overview',
  'content',
  'schema',
  'summary',
  'preview',
  'statistics',
  'history',
  'lineage',
  'usage',
  'definition',
] as const;
export type CatalogTab = (typeof CATALOG_TABS)[number];

export const TABLE_TABS: CatalogTab[] = [
  'overview',
  'schema',
  'preview',
  'statistics',
  'history',
  'lineage',
  'usage',
  'definition',
];

export const isCatalogTab = (value: unknown): value is CatalogTab =>
  typeof value === 'string' && (CATALOG_TABS as readonly string[]).includes(value);

export const tabsFor = (level: CatalogLevel): CatalogTab[] =>
  level === 'table'
    ? TABLE_TABS
    : level === 'field'
      ? ['summary', 'definition']
      : ['content', 'summary', 'definition'];

export const defaultCatalogTab = (level: CatalogLevel): CatalogTab =>
  level === 'table' ? 'overview' : level === 'field' ? 'summary' : 'content';

/**
 * L'onglet affiché pour un `?tab=` : les liens d'avant le 2026-10-19 vers une table (`content` = ses
 * champs, `summary`) tombent sur leurs successeurs (Schéma, Vue d'ensemble), pas sur un onglet vide.
 */
export function resolveCatalogTab(level: CatalogLevel, tab: CatalogTab | undefined): CatalogTab {
  if (level === 'table' && tab === 'content') return 'schema';
  if (level === 'table' && tab === 'summary') return 'overview';
  return tab && tabsFor(level).includes(tab) ? tab : defaultCatalogTab(level);
}

/** `validateSearch` des routes du catalogue : `?tab=` facultatif (le défaut dépend du niveau). */
export const catalogSearch = (search: Record<string, unknown>): { tab?: CatalogTab } =>
  isCatalogTab(search['tab']) ? { tab: search['tab'] } : {};
